'use client';

import cn from 'classnames';
import Image from 'next/image';
import { AnchorMenu } from 'src/components/ProductDetailed/AnchorMenu';
import { Wrapper } from 'src/components/Wrapper';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { useWindowWidth } from 'src/hooks/useWindowWidth';
import { Layout } from 'src/layout';
import { Analog } from 'src/types/backendContent';
import { About } from 'src/uikit/About';
import { Benefits } from 'src/uikit/Benefits';
import { Button } from 'src/uikit/Button';
import { Variant } from 'src/uikit/Button/constants';
import { DemoSection } from 'src/uikit/DemoSection';
import { Form, FormForEnum } from 'src/uikit/Form';
import { FullScreen } from 'src/uikit/FullScreen';
import { ButtonProps, HeroInnerPage } from 'src/uikit/HeroInnerPage';
import { Checked } from 'src/uikit/icons/ContentIcons/TechIcons/States/Checked';
import { ProductsList } from 'src/uikit/ProductsList';
import { Table } from 'src/uikit/Table';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import s from './Analog.module.scss';

const VIDEO_BLOCK_DATA = {
  title: 'Безопасность облака Cloud.ru',
  link: 'https://cdn-video.cloud.ru/backend/video/security/data_center_cloud.mp4',
  previewImage:
    'https://cdn.cloud.ru/backend/images/pages/promo/products/obs/preview-video.png?v=1',
};

const CONTENT_DATA = {
  firstSection: {
    title: 'Cloud․ru — надежный облачный провайдер',
    image:
      'https://cdn.cloud.ru/backend/images/pages/bystryj-start-advanced/content-img-1.png',
  },
};

interface CustomBannerProps {
  title: string;
  withAnalogues: boolean;
  onButtonClickToForm?(): void;
  onButtonClickToServices?(): void;
}

function CustomBanner({
  title,
  withAnalogues,
  onButtonClickToForm,
  onButtonClickToServices,
}: CustomBannerProps) {
  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();

  const image =
    windowWidth && windowWidth > MD
      ? 'https://cdn.cloud.ru/backend/images/pages/analogi/banner-desk.png'
      : 'https://cdn.cloud.ru/backend/images/pages/analogi/banner-mob.png';

  return (
    <section className={s.banner}>
      <Wrapper>
        <div className={s.bannerBlackWrapper}>
          <div>
            <h2 className={s.bannerTitle}>{title}</h2>
            <div className={s.bannerButtons}>
              <Button
                className={s.bannerButton}
                onClick={() => onButtonClickToForm && onButtonClickToForm()}
                analyticsOptions={{
                  action: 'click',
                  clickZone: 'body',
                  clickElement: 'button',
                  clickContent: 'Получить консультацию',
                  uniqueId: `analog-banner-form-button-${title}`,
                  transitionType: 'inside-link',
                  clickZoneCategory: 'site-body',
                }}
              >
                Получить консультацию
              </Button>
              {withAnalogues && (
                <Button
                  className={cn(s.bannerButton, s.white)}
                  onClick={() =>
                    onButtonClickToServices && onButtonClickToServices()
                  }
                  variant={Button.variants.Outline}
                  analyticsOptions={{
                    action: 'click',
                    clickZone: 'body',
                    clickElement: 'button',
                    clickContent: 'Наши аналоги',
                    uniqueId: `analog-banner-service-button-${title}`,
                    transitionType: 'inside-link',
                    clickZoneCategory: 'site-body',
                  }}
                >
                  Наши аналоги
                </Button>
              )}
            </div>
          </div>

          <Image
            className={s.bannerImage}
            width={500}
            height={500}
            src={image}
            alt="image"
          />
        </div>
      </Wrapper>
    </section>
  );
}

export interface AnchorMenuProps {
  id: string;
  name: string;
}

export function AnalogPage({
  analog,
  anchorMenu,
}: {
  analog: Analog;
  anchorMenu: AnchorMenuProps[];
}) {
  const { formRef, setAnchorId } = useIntersectionObserveForms({
    formName: 'lead-analogi',
  });

  const onButtonClickToForm = () => {
    setAnchorId(`analog_hero_anchor_to_form`);
    scrollToElementWithId('form');
  };

  const onButtonClickToServices = () => {
    analog.products &&
      scrollToElementWithId(anchorMenu[anchorMenu.length - 1].id);
  };

  const breadcrumbs = {
    links: [
      {
        title: 'Главная',
        link: '/',
      },
      {
        title: 'Зарубежные аналоги',
        link: '/analogi',
      },
      {
        title: `${analog.title}`,
      },
    ],
  };

  const filteredAcnhors = anchorMenu.filter((anchor) => anchor.id !== '');

  const heroButtons: ButtonProps[] = [];

  const primaryButton = {
    title: 'Получить консультацию',
    onClick: () => onButtonClickToForm(),
    analyticsOptions: {
      action: 'click',
      clickZone: 'body',
      clickElement: 'button',
      clickContent: 'Получить консультацию',
      uniqueId: `analog-heroblock-form-button-${analog.title}`,
      transitionType: 'inside-link',
      clickZoneCategory: 'site-body',
    },
  } satisfies ButtonProps;

  heroButtons.push(primaryButton);

  if (!!analog.products) {
    heroButtons.push({
      title: 'Наши аналоги',
      onClick: () => onButtonClickToServices(),
      variant: Variant.Tertiary,
      analyticsOptions: {
        action: 'click',
        clickZone: 'body',
        clickElement: 'button',
        clickContent: 'Наши аналоги',
        uniqueId: `analog-heroblock-service-button-${analog.title}`,
        transitionType: 'inside-link',
        clickZoneCategory: 'site-body',
      },
    } satisfies ButtonProps);
  }

  return (
    <Layout>
      <div className={s.root}>
        <AnchorMenu slug={analog.slug} anchors={filteredAcnhors} />

        <section className={s.content}>
          <section className={s.contentWrapper}>
            <HeroInnerPage
              title={analog.title}
              description={analog?.description || ''}
              heroImage=""
              breadcrumbs={breadcrumbs}
              buttons={heroButtons}
            />

            {analog.content.blocks &&
              analog.content.blocks.map(({ data, type }, index) => {
                switch (type) {
                  case 'dividedBlock':
                    return (
                      <About
                        key={anchorMenu[index].id}
                        title={data.title}
                        description={data.texts
                          .map((str) => `<p class="aboutText">${str}</p>`)
                          .join('')}
                        id={anchorMenu[index].id}
                      />
                    );
                  case 'customList':
                    return data.isNumeric ? (
                      <Benefits
                        key={anchorMenu[index].id}
                        id={anchorMenu[index].id}
                        title={data.title}
                        cardWithNumeric
                        cards={data.listItems.map((item) => {
                          return {
                            title: '',
                            text: item.description,
                          };
                        })}
                      />
                    ) : (
                      <Benefits
                        key={anchorMenu[index].id}
                        id={anchorMenu[index].id}
                        title={data.title}
                        cards={data.listItems.map((item) => {
                          return {
                            title: item.title,
                            text: item.description,
                            icon: item.icon || Checked,
                          };
                        })}
                      />
                    );
                  case 'cards':
                    return (
                      <Benefits
                        key={anchorMenu[index].id}
                        title={data.title}
                        cards={data.cards.map((item) => {
                          return {
                            title: item.title,
                            text: item.description,
                          };
                        })}
                        cardWithShadow
                        grid={Benefits.grid.ThreeColumns}
                      />
                    );
                  case 'banner':
                    return (
                      <CustomBanner
                        key={anchorMenu[index].id}
                        title={data.title}
                        onButtonClickToForm={onButtonClickToForm}
                        onButtonClickToServices={onButtonClickToServices}
                        withAnalogues={!!analog.products}
                      />
                    );
                  case 'table':
                    return (
                      <Table
                        key={anchorMenu[index].id}
                        title="Сравнение с нашим аналогом"
                        rows={data.content}
                        isComparison
                      />
                    );
                }
              })}
          </section>

          <DemoSection
            className={s.contentBlock}
            type={DemoSection.type.List}
            title={CONTENT_DATA.firstSection.title}
            image={CONTENT_DATA.firstSection.image}
            items={[
              {
                title: 'Один из ведущих на рынке облачных сервисов',
                description: 'по данным исследования iKS-Consulting, 2021',
              },
              {
                title: '80+',
                description: 'IaaS-, PaaS-сервисы',
              },
              {
                title: 'Пять дата-центров в России',
                description: 'с высоким уровнем отказоустойчивости',
              },
              {
                title: '1 000+ экспертов',
                description: 'для решения бизнес-задач в различных отраслях',
              },
            ]}
          />

          <FullScreen
            title={VIDEO_BLOCK_DATA.title}
            videoLink={VIDEO_BLOCK_DATA.link}
            controls
          />

          {analog.products && analog.products.length > 0 && (
            <div id={anchorMenu[anchorMenu.length - 1].id}>
              <ProductsList
                title="Сервисы"
                // TODO: поправить ручку на бэке, чтобы возвращала type
                products={analog.products.map((product) => ({
                  ...product,
                  type: 'products',
                }))}
                cardsWithShadow
              />
            </div>
          )}

          {analog && analog.form && (
            <Form
              id="form"
              ref={formRef}
              formFor={FormForEnum.Registry}
              inputPath="v2/order"
              title={analog.form.title}
              subtitle={`Мы подберем аналоги сервиса ${analog.title} под ваши задачи`}
              buttonTitle={analog.form.buttonTitle}
              fields={analog.form.fields}
              successNotice={{
                title: analog.form.successText,
              }}
              errorNotice={{
                title: analog.form.failedText,
              }}
              analyticsFieldFill={{ formType: 'body' }}
              allFormsSentFormType="consultation"
              formVariant="inner"
              successfulSubmissonFormAnalytics={{
                formname: 'lead-analogi',
              }}
            />
          )}
        </section>
      </div>
    </Layout>
  );
}
